import { useEffect, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from '@/ui';
import { isValidLinkedin, sanitizeUserProfileUpdate } from '@/utils';

import Growth from './Growth';
import Profile from './Profile';

import type { User, UserAssesment, UserProfile } from '@/types';
import type {
  SeatChangePreview,
  SeatChangeReview,
} from '@/types/memberSeatChange';
import type { FC } from 'react';

import SeatChangeReviewDialog, {
  canConfirmSeatChange,
} from '@/components/Profile/Client/SeatChangeReview';
import Bio from '@/components/Profile/Edit/Bio';
import {
  usePreviewMemberSeatChangeMutation,
  userApi,
  useUpdateUserAssessmentMutation,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
} from '@/store/api/userApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  isUncertainSeatChangeSave,
  seatChangeReviewErrorMessage,
} from '@/types/seatChangePreview';

interface EditClientProps {
  client: User;
  onClose: () => void;
}

interface ReviewedProfileSave {
  id: string;
  userProfile: Partial<UserProfile>;
  seatChangeReview: SeatChangeReview;
}

const EditClientForm: FC<EditClientProps> = ({
  client: currentClient,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const isSelf = useAppSelector(
    (state) =>
      userApi.endpoints.getCurrentUser.select()(state).data?.id ===
      currentClient.id,
  );

  const [isSaving, setIsSaving] = useState(false);
  const busy = useRef(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
    };
  }, []);
  const [preview, setPreview] = useState<SeatChangePreview>();
  const [needsReview, setNeedsReview] = useState(false);
  const [seatPublished, setSeatPublished] = useState(false);
  const [uncertainSave, setUncertainSave] = useState<ReviewedProfileSave>();
  const [saveError, setSaveError] = useState('');
  const [savedPayloads, setSavedPayloads] = useState<string[]>([]);
  const [savedProfile, setSavedProfile] = useState<UserProfile>();
  const [persistedSeatType, setPersistedSeatType] = useState(
    currentClient.profile?.seatType,
  );
  const [client, setClient] = useState(currentClient);
  const [profile, setProfile] = useState(
    currentClient.profile ?? ({} as UserProfile),
  );
  const [assesment, setAssesment] = useState(
    currentClient.userAssesment ?? ({} as UserAssesment),
  );

  const [updateUser] = useUpdateUserMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateUserAssessment] = useUpdateUserAssessmentMutation();
  const [previewSeatChange] = usePreviewMemberSeatChangeMutation();

  const invalidateReview = () => {
    generation.current += 1;
    setPreview(undefined);
  };
  const onChange = (changed: User) => {
    invalidateReview();
    setClient(changed);
  };
  const onProfileChange = (changed: UserProfile) => {
    invalidateReview();
    setProfile(changed);
  };

  const onAssessmentChange = (changed: UserAssesment) => {
    invalidateReview();
    setAssesment(changed);
  };

  const onCancel = () => onClose();
  const onSave = async (
    review?: SeatChangeReview,
    replay?: ReviewedProfileSave,
  ) => {
    if (busy.current || (uncertainSave && !replay)) return;
    if (
      !replay &&
      review &&
      (!preview ||
        !canConfirmSeatChange(preview, review.bufferWaiverAppointmentIds))
    ) {
      setPreview(undefined);
      setNeedsReview(true);
      setSaveError(
        'Calendar review expired or changed. Request a fresh review.',
      );
      return;
    }
    busy.current = true;
    const requestGeneration = generation.current;
    const isCurrent = () =>
      mounted.current && requestGeneration === generation.current;
    const finish = () => {
      busy.current = false;
      if (mounted.current) setIsSaving(false);
    };
    setIsSaving(true);
    setSaveError('');

    const [firstName, ...rest] = client.fullName.split(' ');
    const lastName = rest.join(' ');
    const userToUpdate = {
      ...client,
      firstName,
      lastName,
    };

    // Replay only the original request. A matching seat reread does not prove its other attributes committed.
    const reviewedRequest =
      replay ??
      (review
        ? structuredClone({
            id: client.id,
            userProfile: sanitizeUserProfileUpdate(profile),
            seatChangeReview: review,
          })
        : undefined);
    const profileToUpdate =
      reviewedRequest?.userProfile ?? sanitizeUserProfileUpdate(profile);
    const requiresPreview =
      !!profile.seatType &&
      profile.seatType !== persistedSeatType &&
      !reviewedRequest;
    const payloads = [userToUpdate, profileToUpdate, assesment].map((payload) =>
      JSON.stringify(payload),
    );
    const results = await Promise.allSettled([
      reviewedRequest || savedPayloads[0] === payloads[0]
        ? Promise.resolve()
        : updateUser({ id: client.id, user: userToUpdate }).unwrap(),
      requiresPreview || (!reviewedRequest && savedPayloads[1] === payloads[1])
        ? Promise.resolve()
        : updateUserProfile(
            reviewedRequest ?? {
              id: client.id,
              userProfile: profileToUpdate,
              seatChangeReview: review,
            },
          ).unwrap(),
      reviewedRequest || savedPayloads[2] === payloads[2]
        ? Promise.resolve()
        : updateUserAssessment({
            id: client.id,
            userAssesment: assesment,
          }).unwrap(),
    ]);
    if (!mounted.current) return;
    setSavedPayloads((previous) =>
      payloads.map((payload, index) =>
        !(requiresPreview && index === 1) &&
        !(reviewedRequest && index !== 1) &&
        results[index]?.status === 'fulfilled'
          ? payload
          : (previous[index] ?? ''),
      ),
    );
    if (!requiresPreview && results[1].status === 'fulfilled') {
      setPersistedSeatType(
        results[1].value?.seatType ?? profileToUpdate.seatType,
      );
      if (reviewedRequest) {
        setUncertainSave(undefined);
        setPreview(undefined);
        setNeedsReview(false);
        setSeatPublished(true);
      }
    }
    const labels = ['Name and account', 'Profile', 'Growth assessment'];
    const failures = results.flatMap((result, index) =>
      result.status === 'rejected'
        ? [`${labels[index]} could not be saved.`]
        : [],
    );
    if (!isCurrent() && !reviewedRequest) {
      finish();
      return;
    }
    if (requiresPreview && profile.seatType && !failures.length) {
      try {
        const nextPreview = await previewSeatChange({
          id: client.id,
          targetSeatType: profile.seatType,
        }).unwrap();
        if (isCurrent()) {
          const previous =
            persistedSeatType === 'standard_leadership'
              ? 'leadership'
              : persistedSeatType;
          if (previous && nextPreview.previousSeatType !== previous)
            throw new Error('mismatched review');
          setPreview(nextPreview);
          setNeedsReview(false);
        }
      } catch (error) {
        if (isCurrent()) {
          setNeedsReview(true);
          setSaveError(seatChangeReviewErrorMessage(error));
        }
      } finally {
        finish();
      }
      return;
    }
    const uncertain =
      !!reviewedRequest &&
      results[1].status === 'rejected' &&
      isUncertainSeatChangeSave(results[1].reason);
    if (reviewedRequest && results[1].status === 'rejected') {
      setPreview(undefined);
      setUncertainSave(uncertain ? reviewedRequest : undefined);
      setNeedsReview(!uncertain);
      failures[0] = uncertain
        ? 'Save outcome is unconfirmed. Retry the original request to check whether it was saved. New edits will not be included.'
        : seatChangeReviewErrorMessage(results[1].reason);
      if (uncertain) setSaveError(failures[0]);
    }
    try {
      const refreshed = await dispatch(
        userApi.endpoints.getUser.initiate(client.id, {
          forceRefetch: true,
          subscribe: false,
        }),
      ).unwrap();
      if (
        isCurrent() &&
        reviewedRequest &&
        results[1].status === 'rejected' &&
        refreshed.profile?.seatType
      )
        setPersistedSeatType(refreshed.profile.seatType);
    } catch {
      failures.push(
        'Unable to refresh this member. Reopen the profile to check saved changes.',
      );
    }
    if (isSelf) {
      try {
        await dispatch(
          userApi.endpoints.getCurrentUser.initiate(undefined, {
            forceRefetch: true,
            subscribe: false,
          }),
        ).unwrap();
      } catch {
        failures.push('Unable to refresh your profile.');
      }
    }
    // A failed or stale full-user reread must not erase a successful profile PUT.
    const profileResult = results[1];
    let authoritativeProfile = reviewedRequest ? undefined : savedProfile;
    if (profileResult.status === 'fulfilled' && profileResult.value)
      authoritativeProfile = profileResult.value;
    if (authoritativeProfile) {
      setSavedProfile(authoritativeProfile);
      dispatch(
        userApi.util.updateQueryData('getUser', client.id, (cached) => ({
          ...cached,
          profile: { ...cached.profile, ...authoritativeProfile },
        })),
      );
      dispatch(
        userApi.util.updateQueryData('getCurrentUser', undefined, (cached) => {
          if (cached.id === client.id)
            return {
              ...cached,
              profile: { ...cached.profile, ...authoritativeProfile },
            };
          return cached;
        }),
      );
    }
    finish();
    if (!isCurrent()) return;
    if (failures.length) {
      setSaveError(
        uncertain
          ? failures.join(' ')
          : `${failures.join(' ')} Successful changes have been retained; retry saves only unsaved changes.`,
      );
    } else if (
      reviewedRequest &&
      (JSON.stringify(sanitizeUserProfileUpdate(profile)) !== payloads[1] ||
        payloads[0] !== savedPayloads[0] ||
        payloads[2] !== savedPayloads[2])
    ) {
      setSaveError(
        'Original save confirmed. Your newer edits have not been saved.',
      );
    } else {
      onClose();
    }
  };

  const linkedIn = profile.linkedIn || '';

  const isDisabled = !!linkedIn && !isValidLinkedin(linkedIn);

  return (
    <>
      <Dialog maxWidth="md" fullWidth open>
        <DialogContent>
          <Profile
            client={client}
            persistedSeatType={persistedSeatType}
            profile={profile}
            onChange={onChange}
            onProfileChange={onProfileChange}
          />

          <Divider className="my-8" />
          <Bio profile={profile} onProfileChange={onProfileChange} />
          <Divider className="my-8" />
          <Growth
            assesment={assesment}
            onAssessmentChange={onAssessmentChange}
          />
          {saveError && <Typography role="alert">{saveError}</Typography>}
          {seatPublished && (
            <Typography role="status">
              Seat saved; calendar updates pending verification.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {uncertainSave && (
            <Button
              disabled={isSaving}
              loading={isSaving}
              variant="contained"
              onClick={() => onSave(undefined, uncertainSave)}
            >
              Retry original seat-change save
            </Button>
          )}
          <Button
            color="primary"
            disabled={isSaving}
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={isDisabled || isSaving || !!preview || !!uncertainSave}
            loading={isSaving}
            variant="contained"
            onClick={() => onSave()}
          >
            {needsReview && profile.seatType !== persistedSeatType
              ? 'Review calendar changes'
              : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {preview && (
        <SeatChangeReviewDialog
          key={`${preview.previewId}:${preview.previewDigest}`}
          preview={preview}
          saving={isSaving}
          onCancel={() => setPreview(undefined)}
          onConfirm={(review) => onSave(review)}
          onRefresh={() => {
            setPreview(undefined);
            onSave();
          }}
        />
      )}
    </>
  );
};

const EditClient: FC<EditClientProps> = ({ client, ...props }) => (
  <EditClientForm key={client.id} client={client} {...props} />
);

export default EditClient;
